import React, { useEffect, useState } from "react";
import {
  Alert,
  Checkbox,
  Divider,
  InputNumber,
  message,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { updatePartnerRevenueShare } from "../../Pages/api";

const { Text } = Typography;

const UpdateRevenueShareModal = ({ visible, onClose, partner, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [useDefault, setUseDefault] = useState(false);
  const [revenueShare, setRevenueShare] = useState(null);

  useEffect(() => {
    if (visible && partner) {
      // If partner has a custom revenue share, use it, otherwise default to null (use product default)
      if (
        partner.commonReferralRevenueShare !== null &&
        partner.commonReferralRevenueShare !== undefined
      ) {
        setRevenueShare(partner.commonReferralRevenueShare);
        setUseDefault(false);
      } else {
        setRevenueShare(null);
        setUseDefault(true);
      }
    }
  }, [visible, partner]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const valueToSend = useDefault ? null : revenueShare;

      const response = await updatePartnerRevenueShare(
        partner._id,
        valueToSend
      );

      if (response?.code === 200) {
        message.success("Revenue share updated successfully");
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        message.error(response?.message || "Failed to update revenue share");
      }
    } catch (err) {
      console.error(err);
      message.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setUseDefault(false);
    setRevenueShare(null);
    onClose();
  };

  return (
    <Modal
      title="Update Revenue Share"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      okButtonProps={{
        disabled: !useDefault && (revenueShare === null || revenueShare < 0),
      }}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Text strong>Partner Details:</Text>
          <div style={{ marginTop: 8 }}>
            <Text>{partner?.name}</Text>
            <br />
            <Text type="secondary">{partner?.email}</Text>
            <br />
            <Text type="secondary">{partner?.phone}</Text>
            <br />
            <Text type="secondary">({partner?.host})</Text>
          </div>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        {/* Current Revenue Share Status */}
        <div>
          <Text strong>Current Revenue Share:</Text>
          <div style={{ marginTop: 8 }}>
            {partner?.commonReferralRevenueShare !== null &&
            partner?.commonReferralRevenueShare !== undefined ? (
              <>
                <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                  {partner.commonReferralRevenueShare}%
                </Tag>
                {partner?.commonReferralRevenueShareUpdatedAt && (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginTop: 4, fontSize: 12 }}
                  >
                    Last updated:{" "}
                    {moment(partner.commonReferralRevenueShareUpdatedAt).format(
                      "DD MMM YYYY, hh:mm A"
                    )}
                  </Text>
                )}
              </>
            ) : (
              <Tag
                color="default"
                style={{ fontSize: 14, padding: "4px 12px" }}
              >
                Using Product Default
              </Tag>
            )}
          </div>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <div>
          <Text strong style={{ fontSize: 14 }}>
            Update Revenue Share:
          </Text>
        </div>

        <div>
          <Checkbox
            checked={useDefault}
            onChange={(e) => {
              setUseDefault(e.target.checked);
              if (e.target.checked) {
                setRevenueShare(null);
              }
            }}
          >
            <Text strong>Use Product Default Revenue Share</Text>
          </Checkbox>
        </div>

        {!useDefault && (
          <div>
            <Text strong>Custom Revenue Share (%):</Text>
            <br />
            <InputNumber
              style={{ width: "100%", marginTop: 8 }}
              min={0}
              max={100}
              step={0.01}
              precision={2}
              value={revenueShare}
              onChange={(value) => setRevenueShare(value)}
              placeholder="Enter revenue share percentage"
              addonAfter="%"
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginTop: 4 }}
            >
              Enter a value between 0 and 100. Use 0 for 0% revenue share.
            </Text>
          </div>
        )}

        <Alert
          message="Note"
          description="Setting to 'Use Product Default' will reset the revenue share to use the default value from the product settings. This will apply to all future earnings for this partner."
          type="info"
          showIcon
        />
      </Space>
    </Modal>
  );
};

UpdateRevenueShareModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partner: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    host: PropTypes.string,
    commonReferralRevenueShare: PropTypes.number,
    commonReferralRevenueShareUpdatedAt: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

UpdateRevenueShareModal.defaultProps = {
  partner: null,
  onSuccess: () => {},
};

export default UpdateRevenueShareModal;
